import { cn } from '@/lib/utils';
import { useEffect, useMemo, useState } from 'react';
import {
  sectionAnchorId,
  type ChallengeEditorSectionId,
  type ChallengeEditorSectionMeta,
} from './sectionRegistry';
import type { ChallengeEditorErrors, ChallengeEditorValues } from './types';

interface SectionNavProps {
  sections: readonly ChallengeEditorSectionMeta[];
  values: ChallengeEditorValues;
  errors: ChallengeEditorErrors;
}

type SectionDotState = 'complete' | 'error' | 'incomplete';

function dotState(
  section: ChallengeEditorSectionMeta,
  values: ChallengeEditorValues,
  errors: ChallengeEditorErrors,
): SectionDotState {
  if (section.errorFields.some((field) => errors[field])) return 'error';
  return section.complete(values) ? 'complete' : 'incomplete';
}

const DOT_CLASSES: Record<SectionDotState, string> = {
  complete: 'bg-emerald-500 border-emerald-500',
  error: 'bg-destructive border-destructive',
  incomplete: 'bg-transparent border-muted-foreground/40',
};

/**
 * Sticky left rail for the challenge editor (hidden below `lg`). One
 * IntersectionObserver drives the scrollspy; each entry shows a state dot —
 * green filled when the section is complete, red when it owns a validation
 * error, hollow otherwise.
 */
export function SectionNav({ sections, values, errors }: SectionNavProps) {
  const [activeId, setActiveId] = useState<ChallengeEditorSectionId | null>(
    sections[0]?.id ?? null,
  );

  const sectionIds = useMemo(() => sections.map((s) => s.id), [sections]);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return undefined;

    const visibility = new Map<string, number>();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          visibility.set(entry.target.id, entry.isIntersecting ? entry.intersectionRatio : 0);
        }
        // Highlight the first (topmost in registry order) visible section.
        for (const id of sectionIds) {
          if ((visibility.get(sectionAnchorId(id)) ?? 0) > 0) {
            setActiveId(id);
            return;
          }
        }
      },
      // Focus band near the top of the viewport so the active entry tracks
      // the section the admin is actually reading.
      { rootMargin: '-15% 0px -65% 0px', threshold: [0, 0.01, 0.5] },
    );

    for (const id of sectionIds) {
      const el = document.getElementById(sectionAnchorId(id));
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [sectionIds]);

  const scrollTo = (id: ChallengeEditorSectionId) => {
    document
      .getElementById(sectionAnchorId(id))
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <nav
      aria-label="Challenge sections"
      className="hidden lg:block sticky top-0 self-start pt-1"
    >
      <ul className="space-y-1">
        {sections.map((section) => {
          const state = dotState(section, values, errors);
          const isActive = section.id === activeId;
          return (
            <li key={section.id}>
              <button
                type="button"
                onClick={() => scrollTo(section.id)}
                className={cn(
                  'flex w-full items-center gap-2.5 rounded-md px-3 py-1.5 text-left transition-colors',
                  'font-mono text-xs uppercase tracking-wider',
                  isActive
                    ? 'bg-muted text-foreground'
                    : 'text-muted-foreground hover:bg-muted/60 hover:text-foreground',
                )}
              >
                <span
                  aria-hidden
                  className={cn(
                    'h-2 w-2 shrink-0 rounded-full border-2',
                    DOT_CLASSES[state],
                  )}
                />
                {section.navLabel}
                {state === 'error' && <span className="sr-only">(has errors)</span>}
              </button>
            </li>
          );
        })}
      </ul>
    </nav>
  );
}
